import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';

import { ActionButton } from '@/shared/ui/action-button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import type { AttachmentType } from '@/entities/consignments';

import { AttachmentUploadModal } from '@/features/consignment-attachment-upload';
import { AttachmentList, type AttachmentsData } from '@/features/consignment-attachment-list';

interface AttachmentsCardProps {
  consignmentId?: string;
  isReadOnly?: boolean;
  onAttachmentsChange?: (attachments: AttachmentsData) => void;
}

export function AttachmentsCard({ consignmentId, isReadOnly = false, onAttachmentsChange }: AttachmentsCardProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const attachmentsRef = useRef<AttachmentsData | null>(null);

  const handleAttachmentsChange = (attachments: AttachmentsData) => {
    attachmentsRef.current = attachments;
    onAttachmentsChange?.(attachments);
  };

  const handleAddAttachment = (file: File, attachmentType: AttachmentType, comment?: string) => {
    if (attachmentsRef.current) {
      attachmentsRef.current.addLocalAttachment(file, attachmentType, comment);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">{t('newcmr.attachments.label')}</CardTitle>
        </CardHeader>

        <CardContent>
          <AttachmentList consignmentId={consignmentId} onAttachmentsChange={handleAttachmentsChange} />
          {!isReadOnly && (
            <ActionButton
              type="button"
              intent="document"
              onClick={() => setIsModalOpen(true)}
              className="w-full sm:w-auto">
              <Upload />
              {t('newcmr.add.attachment.label')}
            </ActionButton>
          )}
        </CardContent>
      </Card>

      <AttachmentUploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAddAttachment={handleAddAttachment}
        onSuccess={() => setIsModalOpen(false)}
      />
    </>
  );
}
